// Types matching the exact structure of your locationData
type LocationData = Array<{
  [provinceName: string]: Array<{
    [districtName: string]: Array<{
      [sectorName: string]: Array<{
        [cellName: string]: string[];
      }>;
    }>;
  }>;
}>;

export interface LocationSelection {
  province?: string;
  district?: string;
  sector?: string;
  cell?: string;
  village?: string;
}

export class RwandaLocationHelper {
  private locationData: LocationData;

  constructor(locationData: LocationData) {
    this.locationData = locationData;
  }

  // Get all provinces
  getProvinces(): string[] {
    const provinces: string[] = [];
    this.locationData.forEach((provinceObj) => {
      provinces.push(...Object.keys(provinceObj));
    });
    return provinces;
  }

  // Get districts for a specific province
  getDistricts(provinceName: string): string[] {
    if (!provinceName) return [];
    
    const districts: string[] = [];
    this.locationData.forEach((provinceObj) => {
      if (provinceObj[provinceName]) {
        provinceObj[provinceName].forEach((districtObj) => {
          districts.push(...Object.keys(districtObj));
        });
      }
    });
    return districts;
  }

  // Get sectors for a specific district
  getSectors(provinceName: string, districtName: string): string[] {
    if (!provinceName || !districtName) return [];
    
    const sectors: string[] = [];
    this.locationData.forEach((provinceObj) => {
      if (provinceObj[provinceName]) {
        provinceObj[provinceName].forEach((districtObj) => {
          if (districtObj[districtName]) {
            districtObj[districtName].forEach((sectorObj) => {
              sectors.push(...Object.keys(sectorObj));
            });
          }
        });
      }
    });
    return sectors;
  }

  // Get cells for a specific sector
  getCells(provinceName: string, districtName: string, sectorName: string): string[] {
    if (!provinceName || !districtName || !sectorName) return [];
    
    const cells: string[] = [];
    this.locationData.forEach((provinceObj) => {
      if (provinceObj[provinceName]) {
        provinceObj[provinceName].forEach((districtObj) => {
          if (districtObj[districtName]) {
            districtObj[districtName].forEach((sectorObj) => {
              if (sectorObj[sectorName]) {
                sectorObj[sectorName].forEach((cellObj) => {
                  cells.push(...Object.keys(cellObj));
                });
              }
            });
          }
        });
      }
    });
    return cells;
  }

  // Get villages for a specific cell
  getVillages(provinceName: string, districtName: string, sectorName: string, cellName: string): string[] {
    if (!provinceName || !districtName || !sectorName || !cellName) return [];
    
    let villages: string[] = [];
    this.locationData.forEach((provinceObj) => {
      if (provinceObj[provinceName]) {
        provinceObj[provinceName].forEach((districtObj) => {
          if (districtObj[districtName]) {
            districtObj[districtName].forEach((sectorObj) => {
              if (sectorObj[sectorName]) {
                sectorObj[sectorName].forEach((cellObj) => {
                  if (cellObj[cellName]) {
                    villages = cellObj[cellName];
                  }
                });
              }
            });
          }
        });
      }
    });
    return villages;
  }

  // Get next level options based on current selection
  getNextLevelOptions(selection: LocationSelection): string[] {
    const { province, district, sector, cell } = selection;

    if (!province) {
      return this.getProvinces();
    }
    
    if (!district) {
      return this.getDistricts(province);
    }
    
    if (!sector) {
      return this.getSectors(province, district);
    }
    
    if (!cell) {
      return this.getCells(province, district, sector);
    }
    
    return this.getVillages(province, district, sector, cell);
  }

  // Validate if a selection path exists
  isValidSelection(selection: LocationSelection): boolean {
    const { province, district, sector, cell, village } = selection;

    if (province && !this.getProvinces().includes(province)) return false;
    if (district && !this.getDistricts(province!).includes(district)) return false;
    if (sector && !this.getSectors(province!, district!).includes(sector)) return false;
    if (cell && !this.getCells(province!, district!, sector!).includes(cell)) return false;
    if (village && !this.getVillages(province!, district!, sector!, cell!).includes(village)) return false;

    return true;
  }

  // Search functionality across all levels
  searchLocations(query: string, level: 'province' | 'district' | 'sector' | 'cell' | 'village' = 'village'): LocationSelection[] {
    const results: LocationSelection[] = [];
    const searchTerm = query.toLowerCase();

    this.locationData.forEach((provinceObj) => {
      Object.keys(provinceObj).forEach((provinceName) => {
        if (level === 'province' && provinceName.toLowerCase().includes(searchTerm)) {
          results.push({ province: provinceName });
          return;
        }

        provinceObj[provinceName].forEach((districtObj) => {
          Object.keys(districtObj).forEach((districtName) => {
            if (level === 'district' && districtName.toLowerCase().includes(searchTerm)) {
              results.push({ province: provinceName, district: districtName });
              return;
            }

            districtObj[districtName].forEach((sectorObj) => {
              Object.keys(sectorObj).forEach((sectorName) => {
                if (level === 'sector' && sectorName.toLowerCase().includes(searchTerm)) {
                  results.push({ province: provinceName, district: districtName, sector: sectorName });
                  return;
                }

                sectorObj[sectorName].forEach((cellObj) => {
                  Object.keys(cellObj).forEach((cellName) => {
                    if (level === 'cell' && cellName.toLowerCase().includes(searchTerm)) {
                      results.push({ 
                        province: provinceName, 
                        district: districtName, 
                        sector: sectorName, 
                        cell: cellName 
                      });
                      return;
                    }

                    if (level === 'village') {
                      cellObj[cellName].forEach((villageName) => {
                        if (villageName.toLowerCase().includes(searchTerm)) {
                          results.push({
                            province: provinceName,
                            district: districtName,
                            sector: sectorName,
                            cell: cellName,
                            village: villageName
                          });
                        }
                      });
                    }
                  });
                });
              });
            });
          });
        });
      });
    });

    return results;
  }
}

// React hook for easy integration
export function useRwandaLocations(locationData: LocationData) {
  const helper = new RwandaLocationHelper(locationData);
  
  return {
    getProvinces: () => helper.getProvinces(),
    getDistricts: (province: string) => helper.getDistricts(province),
    getSectors: (province: string, district: string) => helper.getSectors(province, district),
    getCells: (province: string, district: string, sector: string) => helper.getCells(province, district, sector),
    getVillages: (province: string, district: string, sector: string, cell: string) => 
      helper.getVillages(province, district, sector, cell),
    searchLocations: (query: string, level?: 'province' | 'district' | 'sector' | 'cell' | 'village') =>
      helper.searchLocations(query, level),
    isValidSelection: (selection: LocationSelection) => helper.isValidSelection(selection)
  };
}